import { Post } from '@repo/editor/src/types';
import { create } from 'zustand';

import { DesignPostData } from 'global/types/DesignPostData';

import Api from '../global/postApi';

interface UsePostsStoreState {
  posts: DesignPostData[];
  setPosts: (posts: Post[]) => void;
  addPost: (post: Post) => void;
  updatePost: (post: DesignPostData) => void;
  getPost: (id: string) => DesignPostData | undefined;
  likePost: (id: string) => void;
}

export const usePostsStore = create<UsePostsStoreState>((set, get) => ({
  posts: [],

  setPosts: (posts: Post[]) => {
    const enrichedPosts: DesignPostData[] = posts.map((post, index) => {
      return {
        ...post,
        likeStatus: 'idle',
        feedIndex: index + 1,
      };
    });
    set({ posts: enrichedPosts });
  },

  addPost: (post: Post) => {
    const currentPosts = get().posts;
    set({
      posts: [
        ...currentPosts,
        {
          ...post,
          likeStatus: 'idle',
          feedIndex: currentPosts.length + 1,
        },
      ],
    });
  },

  updatePost: (post) => {
    const updatedPosts = get().posts.map((p) => {
      if (p.id === post.id) {
        return { ...post, feedIndex: p.feedIndex };
      }
      return p;
    });
    set({ posts: updatedPosts });
  },

  getPost: (id) => {
    return get().posts.find((p) => p.id === id);
  },

  likePost: async (id) => {
    const post = get().getPost(id);

    if (!post) {
      return;
    }

    if (post.likeStatus === 'pending') {
      return;
    }

    get().updatePost({ ...post, likeStatus: 'pending' });

    if (post.likedByUser) {
      const response = await Api.unlikePost({ postId: id });
      if (
        response.success === false &&
        response.error &&
        (!response.errorType || response.errorType !== 'NOT_LIKED')
      ) {
        get().updatePost({ ...post, likeStatus: 'rejected' });
        return;
      }

      get().updatePost({
        ...post,
        likedByUser: false,
        likes: Math.max(0, post.likes - 1),
        likeStatus: 'resolved',
      });
    } else {
      const response = await Api.likePost({ postId: id });
      if (
        response.success === false &&
        response.error &&
        (!response.errorType || response.errorType !== 'ALREADY_LIKED')
      ) {
        get().updatePost({ ...post, likeStatus: 'rejected' });
        return;
      }

      get().updatePost({
        ...post,
        likedByUser: true,
        likes: post.likes + 1,
        likeStatus: 'resolved',
      });
    }
  },
}));

export const postsStoreSelector = {
  posts: (state: UsePostsStoreState) => state.posts,
  setPosts: (state: UsePostsStoreState) => state.setPosts,
  addPost: (state: UsePostsStoreState) => state.addPost,
  getPost: (state: UsePostsStoreState) => state.getPost,
  updatePost: (state: UsePostsStoreState) => state.updatePost,
  likePost: (state: UsePostsStoreState) => state.likePost,
};
